// Weer: Open-Meteo codes → GameOn-weertype, windniveaus, cache-afhandeling.
// Port van legacy/app.js sectie 4 (fetchCurrentWeather, getWindDisplay, getWeatherVisual,
// classifyOpenMeteoWeather, readWeatherCache, updateWeatherDisplay, setSimulatedWeather).
import type { WeatherKind, WeatherState, WindLevel } from './types';

export const DEFAULT_COORDS = { lat: 52.5168, lon: 6.0924 } as const; // Zwolle
export const WEATHER_MAX_CACHE_AGE_MS = 12 * 60 * 60 * 1000;
export const WEATHER_REFRESH_AFTER_MS = 30 * 60 * 1000;
export const WEATHER_FETCH_TIMEOUT_MS = 6000;
/** Een antwoord ouder dan dit (bijv. uit de service-worker-cache) is geen live weer. */
export const WEATHER_MAX_OBSERVATION_AGE_MS = 2 * 60 * 60 * 1000;

export interface RawCurrentWeather {
  temperature_2m?: number;
  precipitation?: number;
  weather_code?: number;
  wind_speed_10m?: number;
  /** ISO-tijd (UTC, zonder 'Z') van de meting. */
  time?: string;
}

export const WEATHER_LABELS: Record<WeatherKind, string> = {
  zonnig: 'Zonnig',
  bewolkt: 'Bewolkt',
  regen: 'Regen',
  onweer: 'Onweer',
  hagel: 'Hagel',
  storm: 'Storm',
  sneeuw: 'Sneeuw',
  onbekend: 'Weer onbekend',
};

/** Weertypes waarbij "Droog"-activiteiten verborgen worden. */
export const BAD_WEATHER_KINDS: ReadonlySet<WeatherKind> = new Set(['regen', 'onweer', 'hagel', 'storm', 'sneeuw']);

/** Standaardtemperaturen van de simulatieknoppen (legacy index.html data-temp). */
export const SIMULATION_DEFAULT_TEMP_C: Record<WeatherKind, number | null> = {
  zonnig: 22,
  bewolkt: 17,
  regen: 14,
  onweer: 16,
  hagel: 4,
  storm: 13,
  sneeuw: -1,
  onbekend: null,
};

/** Standaard windsnelheid per niveau (simulatie en weergave als km/u onbekend is). */
export const WIND_DEFAULT_KMH: Record<WindLevel, number> = { weinig: 10, gemiddeld: 24, veel: 54 };

// weinig (< 20 km/u), gemiddeld (20–39 km/u), veel (>= 40 km/u)
export function windLevelFromKmh(kmh: number): WindLevel {
  if (!Number.isFinite(kmh)) return 'weinig';
  if (kmh >= 40) return 'veel';
  if (kmh >= 20) return 'gemiddeld';
  return 'weinig';
}

/** Legacy niveaunamen ('storm', 'hard', 'matig', ...) → WindLevel. */
export function normalizeWindLevel(level: unknown): WindLevel {
  const l = String(level ?? '').toLowerCase().trim();
  if (l === 'veel' || l === 'storm' || l === 'hard') return 'veel';
  if (l === 'gemiddeld' || l === 'matig' || l === 'normaal') return 'gemiddeld';
  return 'weinig';
}

export interface WindDisplay {
  level: WindLevel;
  /** "Veel wind", "Gemiddeld", "Weinig wind" of "Wind onbekend". */
  label: string;
  /** km/u; 0 blijft 0 (alleen null/onbekend valt terug op een standaard). */
  speed: number | null;
  /** "Gemiddelde wind (24 km/u)". */
  fullText: string;
}

/** Port van getWindDisplay, maar 0 km/u wordt niet vervangen door een standaardwaarde. */
export function windDisplay(level: WindLevel, kmh: number | null, unknown = false): WindDisplay {
  const lvl = normalizeWindLevel(level);
  if (unknown) return { level: lvl, label: 'Wind onbekend', speed: null, fullText: 'Wind onbekend' };
  const fallback = lvl === 'veel' ? 52 : lvl === 'gemiddeld' ? 24 : 10;
  const speed = kmh !== null && Number.isFinite(kmh) ? kmh : fallback;
  if (lvl === 'veel') return { level: lvl, label: 'Veel wind', speed, fullText: `Veel wind (${speed} km/u)` };
  if (lvl === 'gemiddeld') return { level: lvl, label: 'Gemiddeld', speed, fullText: `Gemiddelde wind (${speed} km/u)` };
  return { level: lvl, label: 'Weinig wind', speed, fullText: `Weinig wind (${speed} km/u)` };
}

/**
 * Open-Meteo (WMO) code → GameOn weertype. Exact de legacy-volgorde:
 * storm (wind ≥ 50, of onweer met wind ≥ 38) · 96/99 hagel · 95 onweer · 71–77/85/86 sneeuw ·
 * 51–67/80–82 regen · neerslag > 0.2 mm (≤ 0 °C = sneeuw) · 2/3/45/48 bewolkt · anders zonnig.
 */
export function classifyWeatherCode(code: number, precipitation: number, windKmh: number, tempC: number | null): WeatherKind {
  if (windKmh >= 50 || (code >= 95 && windKmh >= 38)) return 'storm';
  if (code === 96 || code === 99) return 'hagel';
  if (code === 95) return 'onweer';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'sneeuw';
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'regen';
  if (precipitation > 0.2) return tempC !== null && Number.isFinite(tempC) && tempC <= 0 ? 'sneeuw' : 'regen';
  if (code === 2 || code === 3 || code === 45 || code === 48) return 'bewolkt';
  return 'zonnig';
}

/** Legacy weerstring ('Regen', 'Sneeuwbui', 'Onbekend', ...) → WeatherKind (zelfde volgorde als getWeatherVisual). */
export function kindFromLegacyString(value: unknown): WeatherKind {
  const s = String(value ?? '').toLowerCase();
  if (!s || s.includes('onbekend') || s.includes('unknown')) return 'onbekend';
  if (s.includes('storm') || s.includes('orkaan') || s.includes('gale') || s.includes('windstoot')) return 'storm';
  if (s.includes('hagel') || s.includes('hail') || s.includes('sleet') || s.includes('graupel') || s.includes('ijzel')) return 'hagel';
  if (s.includes('onweer') || s.includes('bliksem') || s.includes('thunder')) return 'onweer';
  if (s.includes('sneeuw') || s.includes('snow')) return 'sneeuw'; // vóór regen: 'sneeuwbui' bevat 'bui'
  if (s.includes('regen') || s.includes('bui') || s.includes('drizzle') || s.includes('rain')) return 'regen';
  if (s.includes('bewolkt') || s.includes('wolk') || s.includes('cloud') || s.includes('mist')) return 'bewolkt';
  return 'zonnig';
}

function finiteOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Zet Open-Meteo `current` om naar WeatherState (source 'live'). */
export function mapOpenMeteo(current: RawCurrentWeather, fetchedAt: number, usesOwnLocation: boolean): WeatherState {
  const rawTemp = finiteOrNull(current.temperature_2m);
  const tempC = rawTemp === null ? null : Math.round(rawTemp);
  const windKmh = Math.round(Number(current.wind_speed_10m ?? 0) || 0);
  const precipitation = Number(current.precipitation ?? 0) || 0;
  const code = Number(current.weather_code ?? 0) || 0;
  const kind = classifyWeatherCode(code, precipitation, windKmh, tempC);
  return {
    kind,
    label: WEATHER_LABELS[kind],
    tempC,
    windKmh,
    windLevel: windLevelFromKmh(windKmh),
    isBadWeather: BAD_WEATHER_KINDS.has(kind),
    source: 'live',
    fetchedAt,
    usesOwnLocation,
  };
}

/**
 * Bouwt een WeatherState voor een weertype. Standaard: bron 'simulatie', temperatuur/wind onbekend.
 * Expliciete waarden in `opts` (ook 0) winnen; zonder `windLevel` wordt het niveau uit `windKmh` afgeleid.
 */
export function weatherFromKind(kind: WeatherKind, opts: Partial<WeatherState> = {}): WeatherState {
  const windKmh = opts.windKmh !== undefined ? opts.windKmh : null;
  const windLevel = opts.windLevel ?? (windKmh !== null ? windLevelFromKmh(windKmh) : 'weinig');
  return {
    tempC: null,
    source: 'simulatie',
    fetchedAt: null,
    usesOwnLocation: false,
    ...opts,
    kind,
    label: WEATHER_LABELS[kind],
    isBadWeather: BAD_WEATHER_KINDS.has(kind),
    windKmh,
    windLevel,
  };
}

export function unknownWeather(usesOwnLocation = false): WeatherState {
  return {
    kind: 'onbekend',
    label: WEATHER_LABELS.onbekend,
    tempC: null,
    windKmh: null,
    windLevel: 'weinig',
    isBadWeather: false,
    source: 'onbekend',
    fetchedAt: null,
    usesOwnLocation,
  };
}

export function buildWeatherUrl(coords: { lat: number; lon: number }): string {
  return `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lon}&current=temperature_2m,precipitation,weather_code,wind_speed_10m`;
}

/** Haalt live weer op met timeout (ms). Gooit bij netwerk-/HTTP-fout, timeout, onvolledig of verouderd antwoord. */
export async function fetchWeather(
  coords: { lat: number; lon: number },
  opts: { timeoutMs?: number; usesOwnLocation: boolean; fetchImpl?: typeof fetch; now?: () => number },
): Promise<WeatherState> {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const now = opts.now ?? Date.now;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), opts.timeoutMs ?? WEATHER_FETCH_TIMEOUT_MS) : null;
  try {
    const res = await fetchImpl(buildWeatherUrl(coords), controller ? { signal: controller.signal } : undefined);
    if (!res.ok) throw new Error('Weer API niet bereikbaar');
    const data = (await res.json()) as { current?: RawCurrentWeather; latitude?: number; longitude?: number } | null;
    const cur = data?.current;
    if (!cur || finiteOrNull(cur.temperature_2m) === null) throw new Error('Onvolledige weerdata');
    // De service worker kan offline een oud antwoord (evt. andere locatie) teruggeven: dat is geen live weer
    const observedAt = Date.parse(String(cur.time ?? '') + 'Z');
    if (Number.isFinite(observedAt) && now() - observedAt > WEATHER_MAX_OBSERVATION_AGE_MS) {
      throw new Error('Verouderd antwoord uit cache');
    }
    const lat = finiteOrNull(data?.latitude);
    const lon = finiteOrNull(data?.longitude);
    if ((lat !== null && Math.abs(lat - coords.lat) > 0.5) || (lon !== null && Math.abs(lon - coords.lon) > 0.5)) {
      throw new Error('Weerdata hoort bij andere locatie');
    }
    return mapOpenMeteo(cur, now(), opts.usesOwnLocation);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

/* ---------- Opmaak ---------- */

/** "18°C"; 0 blijft "0°C"; onbekend = "". */
export function formatTemp(tempC: number | null): string {
  return tempC !== null && Number.isFinite(tempC) ? `${tempC}°C` : '';
}

/** "14:05", "gisteren 14:05" of "3 okt 14:05" (port van formatWeatherTimestamp). */
export function formatWeatherTimestamp(ts: number, now: number): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  const today = new Date(now);
  if (d.toDateString() === today.toDateString()) return time;
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `gisteren ${time}`;
  return `${d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} ${time}`;
}

export function locationLabel(w: WeatherState): string {
  return w.usesOwnLocation ? 'jouw buurt' : 'Zwolle';
}

/**
 * Nederlandse uitleg onder de weerbalk, bijv. "Laatst bekend weer · 14:05".
 * - simulatie: "Simulatie actief"
 * - onbekend:  "Weer onbekend"
 * - cache:     "Laatst bekend weer · 14:05" (+ " · binnen aanbevolen" bij slecht weer)
 * - live:      "Actueel weer in Zwolle" / "Actueel weer in jouw buurt"
 */
export function weatherCaption(w: WeatherState, now: number): string {
  if (w.source === 'simulatie') return 'Simulatie actief';
  if (w.source === 'onbekend' || w.kind === 'onbekend') return 'Weer onbekend';
  if (w.source === 'cache') {
    const stamp = w.fetchedAt !== null ? formatWeatherTimestamp(w.fetchedAt, now) : '';
    const base = stamp ? `Laatst bekend weer · ${stamp}` : 'Laatst bekend weer';
    return w.isBadWeather ? `${base} · binnen aanbevolen` : base;
  }
  return `Actueel weer in ${locationLabel(w)}`;
}

/** Het advies-zinnetje per weertype uit de legacy weerbalk. */
export function weatherAdvice(w: WeatherState, overruled = false): string {
  if (overruled) return 'Weerfilter genegeerd: buitenactiviteiten worden getoond ondanks slecht weer';
  switch (w.kind) {
    case 'onbekend':
      return 'Geen weerdata beschikbaar: alle binnen- en buitenactiviteiten worden getoond';
    case 'storm':
      return 'Stormachtige wind en rukwinden: zoek een veilige, gezellige binnenactiviteit!';
    case 'hagel':
      return 'Hagel en koude buien: blijf lekker warm binnen voor spannende binnenmissies!';
    case 'onweer':
      return 'Onweer en bliksem: blijf veilig binnen, binnenactiviteiten hebben voorrang';
    case 'sneeuw':
      return 'Het sneeuwt: binnenactiviteiten worden met voorrang aanbevolen';
    case 'regen':
      return 'Het regent: binnenactiviteiten worden met voorrang aanbevolen';
    case 'bewolkt':
      return 'Bewolkt maar droog: prima weer voor buitenspelen en binnen';
    default:
      return 'Zonnig en droog: ideaal voor buitenspelen en binnenactiviteiten';
  }
}

/* ---------- Legacy cache (`gameon_weather_cache_v1`) ---------- */

export interface LegacyWeatherCache {
  /** Legacy weerstring, bijv. "Regen". */
  weather: string;
  /** "18°C" of "". */
  temp: string;
  windKmH: number;
  windLevel: string;
  fetchedAt: number;
  lat?: number;
  lon?: number;
}

function parseTempString(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.round(v) : null;
  if (typeof v !== 'string') return null;
  const m = v.match(/-?\d+(?:[.,]\d+)?/);
  if (!m) return null;
  const n = Number(m[0].replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n) : null;
}

/**
 * Valideert de bewaarde cache (string of object). Null als corrupt, ouder dan 12 uur,
 * of van een andere locatie (> 0.1° verschil) dan `coords`.
 */
export function readWeatherCache(
  raw: unknown,
  now: number,
  coords?: { lat: number; lon: number } | null,
  usesOwnLocation = false,
): WeatherState | null {
  let c: unknown = raw;
  if (typeof raw === 'string') {
    try {
      c = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
  const o = c as Record<string, unknown>;
  if (typeof o.weather !== 'string' || typeof o.fetchedAt !== 'number' || !Number.isFinite(o.fetchedAt)) return null;
  if (now - o.fetchedAt > WEATHER_MAX_CACHE_AGE_MS) return null;
  const lat = finiteOrNull(o.lat);
  const lon = finiteOrNull(o.lon);
  if (coords && lat !== null && lon !== null && (Math.abs(lat - coords.lat) > 0.1 || Math.abs(lon - coords.lon) > 0.1)) {
    return null;
  }
  const kind = kindFromLegacyString(o.weather);
  const windKmh = finiteOrNull(o.windKmH) ?? 12;
  const windLevel = typeof o.windLevel === 'string' ? normalizeWindLevel(o.windLevel) : 'weinig';
  return {
    kind,
    label: WEATHER_LABELS[kind],
    tempC: parseTempString(o.temp),
    windKmh,
    windLevel,
    isBadWeather: BAD_WEATHER_KINDS.has(kind),
    source: 'cache',
    fetchedAt: o.fetchedAt,
    usesOwnLocation,
  };
}

/** Maakt het legacy cache-object (zelfde vorm als de oude app schreef). */
export function toWeatherCache(w: WeatherState, coords: { lat: number; lon: number }): LegacyWeatherCache {
  return {
    weather: w.kind === 'onbekend' ? 'Onbekend' : WEATHER_LABELS[w.kind],
    temp: formatTemp(w.tempC),
    windKmH: w.windKmh ?? 0,
    windLevel: w.windLevel,
    fetchedAt: w.fetchedAt ?? 0,
    lat: coords.lat,
    lon: coords.lon,
  };
}

/** Bewaarde apparaatlocatie (`gameon_weather_coords_v1`), of null. */
export function parseStoredCoords(raw: unknown): { lat: number; lon: number } | null {
  let c: unknown = raw;
  if (typeof raw === 'string') {
    try {
      c = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!c || typeof c !== 'object') return null;
  const o = c as Record<string, unknown>;
  const lat = finiteOrNull(o.lat);
  const lon = finiteOrNull(o.lon);
  if (lat === null || lon === null || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}

export function roundCoord(v: number): number {
  return Math.round(v * 100) / 100;
}

/* ---------- Simulatie (legacy losse sleutels) ---------- */

export interface WeatherSimulation {
  weather: { kind: WeatherKind; tempC: number | null } | null;
  wind: { level: WindLevel; kmh: number } | null;
}

/** Leest de legacy simulatiesleutels (allemaal platte strings, zoals de oude app ze schreef). */
export function parseLegacySimulation(v: {
  simulated: string | null;
  state: string | null;
  temp: string | null;
  windSimulated: string | null;
  windLevel: string | null;
  windKmh: string | null;
}): WeatherSimulation {
  const weather =
    v.simulated === '1' && v.state
      ? { kind: kindFromLegacyString(v.state), tempC: v.temp ? parseTempString(v.temp) : null }
      : null;
  let wind: WeatherSimulation['wind'] = null;
  if (v.windSimulated === '1' && v.windLevel) {
    const level = normalizeWindLevel(v.windLevel);
    const kmh = v.windKmh !== null && v.windKmh !== '' ? Number.parseInt(v.windKmh, 10) : Number.NaN;
    wind = { level, kmh: Number.isFinite(kmh) ? kmh : 12 };
  }
  return { weather: weather && weather.kind !== 'onbekend' ? weather : null, wind };
}

/** Legt een simulatie over het live/cache-weer (port van applyLiveWeatherToCurrent + setSimulatedWeather). */
export function applySimulation(base: WeatherState, sim: WeatherSimulation): WeatherState {
  let w = base;
  if (sim.weather) {
    w = {
      ...base,
      kind: sim.weather.kind,
      label: WEATHER_LABELS[sim.weather.kind],
      tempC: sim.weather.tempC,
      isBadWeather: BAD_WEATHER_KINDS.has(sim.weather.kind),
      source: 'simulatie',
    };
    // Storm zonder handmatige wind: veel wind (zoals de oude app)
    if (sim.weather.kind === 'storm' && !sim.wind) w = { ...w, windLevel: 'veel', windKmh: WIND_DEFAULT_KMH.veel };
  }
  if (sim.wind) w = { ...w, windLevel: sim.wind.level, windKmh: sim.wind.kmh };
  return w;
}
